'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';

import { fetchAppConfig } from './app-config.api';
import { AppConfigContext, type AppConfigContextValue } from './app-config.context';
import { applyTheme } from './apply-theme';

import type { AppConfig } from '@utazzo/shared';

const QUERY_KEY = ['app-config'] as const;

const DEFAULT_BOOT_TIMEOUT_MS = 10_000;
const BOOT_RETRY_INTERVAL_MS = 2_000;

interface AppConfigProviderProps {
  /** Base URL of the NestJS API (e.g. `http://localhost:3000/api/v1`). */
  apiBaseUrl: string;
  children: ReactNode;
  fallback?: ReactNode; // Shown while config loads on first render
  /**
   * Config already fetched by the host, handed over so the FIRST render has it.
   *
   * Without it this provider renders `fallback` until its own fetch resolves -
   * and during a server render that fetch never resolves, so the HTML a server
   * sends is the splash alone: no page content for crawlers, and a 200 status
   * on every URL because a page's `notFound()` / `redirect()` is never reached.
   * A server-rendered host (apps/website) must pass this; a client-only one
   * (apps/admin) has no server render to protect and can leave it out.
   */
  initialConfig?: AppConfig;
  errorFallback?: (error: unknown) => ReactNode;
  /**
   * How long to keep retrying `/config` during boot before surfacing an error.
   * Local dev often needs 30–60s while Nest compiles and binds :3000.
   */
  bootTimeoutMs?: number;
  /**
   * When false, the provider still fetches config and exposes it via context,
   * but does NOT apply `theme.colors` to the document. Use this for surfaces
   * with a fixed, code-owned design system (e.g. the internal admin app), so
   * the customer-facing website's CMS branding does not override their tokens.
   * Defaults to true (runtime re-theming on, as the website needs).
   */
  applyRuntimeTheme?: boolean;
}

/**
 * Boots the app by fetching /config from the API, validating it, applying
 * the theme (CSS custom props) and exposing it to the rest of the tree.
 *
 * Order of operations:
 *   1. Fetch /api/v1/config (parallel: no auth needed)
 *   2. Validate against shared Zod schema
 *   3. Apply theme.colors.light → CSS custom props on :root
 *   4. Set document.title from branding.appName
 *   5. Render children
 *
 * On failure: show errorFallback (or a default message). The app does NOT
 * proceed with hardcoded defaults, because that would mask config issues
 * silently.
 */
export const AppConfigProvider = ({
  apiBaseUrl,
  children,
  fallback,
  initialConfig,
  errorFallback,
  bootTimeoutMs = DEFAULT_BOOT_TIMEOUT_MS,
  applyRuntimeTheme = true,
}: AppConfigProviderProps): React.JSX.Element => {
  const queryClient = useQueryClient();
  const maxBootRetries = Math.max(1, Math.ceil(bootTimeoutMs / BOOT_RETRY_INTERVAL_MS));

  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: [...QUERY_KEY, apiBaseUrl] as const,
    queryFn: () => fetchAppConfig(apiBaseUrl),
    initialData: initialConfig,
    staleTime: 5 * 60 * 1000, // 5 min - config rarely changes
    gcTime: 60 * 60 * 1000,
    retry: (failureCount) => failureCount < maxBootRetries,
    retryDelay: () => BOOT_RETRY_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  // Apply theme + document metadata whenever config arrives or changes
  useEffect(() => {
    if (!config) return;
    if (applyRuntimeTheme) applyTheme(config.theme);
    document.title = config.branding.appName;
    if (config.branding.faviconUrl) {
      const link =
        (document.querySelector("link[rel='icon']") as HTMLLinkElement | null) ??
        document.createElement('link');
      link.rel = 'icon';
      link.href = config.branding.faviconUrl;
      if (!link.parentNode) document.head.appendChild(link);
    }
  }, [config, applyRuntimeTheme]);

  useEffect(() => {
    if (error) {
      console.error('[AppConfigProvider] failed to fetch /config:', error);
    }
  }, [error]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, apiBaseUrl] });
  }, [queryClient, apiBaseUrl]);

  const value = useMemo<AppConfigContextValue | null>(
    () => (config ? { config, refresh } : null),
    [config, refresh],
  );

  // Order matters: errors must surface before the loading fallback so a
  // failed /config doesn't leave us spinning forever.
  if (error) {
    return <>{errorFallback ? errorFallback(error) : <DefaultBootError error={error} />}</>;
  }

  if (isLoading || !value) {
    return <>{fallback ?? <DefaultBootSplash />}</>;
  }

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

// =============================================================================
// Default fallbacks. Intentionally minimal - no branding, no copy that could
// become stale. They render BEFORE we have the config, so they cannot use it.
// =============================================================================

const DefaultBootSplash = (): React.JSX.Element => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#6b7280',
    }}
  >
    Loading…
  </div>
);

const DefaultBootError = ({ error }: { error: unknown }): React.JSX.Element => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#374151',
      padding: '2rem',
    }}
  >
    <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Unable to load app config</h1>
    <p style={{ color: '#6b7280', maxWidth: 480, textAlign: 'center' }}>
      The application could not fetch its runtime configuration from the server. If you just started
      the dev servers, wait for the API on port 3000 to finish compiling, then refresh. Contact
      support if the problem persists.
    </p>
    {error instanceof Error && (
      <pre style={{ marginTop: '1rem', color: '#9ca3af', fontSize: '0.75rem' }}>
        {error.message}
      </pre>
    )}
  </div>
);

// Re-export the hook for convenience
export type { AppConfig };
